import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { faMessageXmark } from '@fortawesome/pro-thin-svg-icons';

import { JSONSchema, PlatformApp, PlatformAppUiSchema } from '@typings';
import { PATH } from '@constants';
import { getPlatformAppsTemplates } from '@services';
import {
  contextNamesSelector,
  platformAppsAppInstancesSelector,
  platformAppsLoadersSelector,
} from '@selectors';
import { useSelector } from '@hooks';
import { usePlatformAppsTemplates } from '@hooks/platformApps';
import { as, formatToCamelCase, path, toastifyResponseError } from '@utils';
import { extractIngerationTypesFromSchema } from '@utils/helpers';

import { Button, ChaseSpinner, Helmet, Link, Render, Theme } from '@components';
import { Layout } from '@components/Layouts';
import { EmptyContent } from '@components/Ui';
import { Tags } from '@components/Ui/Job';
import {
  PlatformAppAssets,
  PlatformAppItem,
  PlatformAppItemLink,
  PlatformAppLogo,
  PlatformAppTemplateVersion,
} from '@components/Ui/PlatformApps';

export const PlatformAppsExplorationPage: React.FC = () => {
  const { appName } = useParams();

  const instances = useSelector(platformAppsAppInstancesSelector(appName!));
  const { templatesLoading } = useSelector(platformAppsLoadersSelector);

  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);

  const { template, latestVersion, versions } = usePlatformAppsTemplates();

  const [relatedApps, setRelatedApps] = useState<PlatformApp.Template[]>([]);
  const [isLoadingRelatedApps, setLoadingRelatedApps] = useState(false);

  useEffect(() => {
    if (!template || !organizationName || !clusterName || !projectName) {
      return;
    }

    const integrationInput = extractIngerationTypesFromSchema(template.input);
    const integrationOutput = extractIngerationTypesFromSchema(template.output);

    const types = new Set([
      ...Object.values(integrationInput).map(({ typeName }) => typeName),
      ...Object.values(integrationOutput).map(({ typeName }) => typeName),
    ]);

    types.delete(undefined);
    types.delete(appName);

    if (types.size === 0) {
      return;
    }

    (async () => {
      try {
        setLoadingRelatedApps(true);

        const allRelatedApps = await getPlatformAppsTemplates(
          {
            organizationName,
            clusterName,
            projectName,
            types: Array.from(types as Set<string>),
          },
          { formatResponse: true },
        );

        const relatedApps = allRelatedApps.filter(
          (relatedApp) => relatedApp.name !== appName,
        );

        setRelatedApps(relatedApps);
      } catch (error) {
        toastifyResponseError(error);
      } finally {
        setLoadingRelatedApps(false);
      }
    })();
  }, [appName, clusterName, organizationName, projectName, template]);

  const {
    title,
    logo,
    description,
    shortDescription,
    version,
    pubDate,
    tags,
    documentationUrls,
    externalUrls,
    assets,
  } = as.o<PlatformApp.Template>(formatToCamelCase(template));

  const installedAppCount = instances.length;
  const formattedTitle = `${title || appName} App`;
  const normalizedVersion = version ? encodeURIComponent(version) : null;

  const makeDocumentationLink = ({
    text,
    url,
  }: PlatformApp.Template['documentationUrls'][number]) => (
    <PlatformAppItemLink external key={url} to={url} title={text} />
  );

  const makeReferenceLink = ({
    text,
    url,
  }: PlatformApp.Template['externalUrls'][number]) => (
    <PlatformAppItemLink icon key={url} to={url} title={text} />
  );

  const makeType = ({
    title,
    description,
    logoUrl,
  }: Pick<PlatformAppUiSchema.Model, 'title' | 'description' | 'logoUrl'>) => {
    return (
      <PlatformAppItem
        key={title}
        title={title}
        image={logoUrl}
        description={description}
      />
    );
  };

  const makeIntegrationTypes = useCallback((schema: JSONSchema) => {
    const types = Object.values(extractIngerationTypesFromSchema(schema));

    return types.filter(
      ({ title }, index, self) =>
        index === self.findIndex((item) => item.title === title),
    );
  }, []);

  const inputIntegrationTypes = useMemo(
    () => (template ? makeIntegrationTypes(template.input) : []),
    [template, makeIntegrationTypes],
  );

  const outputIntegrationTypes = useMemo(
    () => (template ? makeIntegrationTypes(template.output) : []),
    [template, makeIntegrationTypes],
  );

  const renderContent = () => {
    if (templatesLoading) {
      return (
        <div className="mt-20 flex justify-center">
          <ChaseSpinner color="black" className="absolute mt-40 h-14 w-14" />
        </div>
      );
    }

    if (!template) {
      return (
        <EmptyContent
          variant="layout"
          icon={faMessageXmark}
          title="App is unavailable"
          text="The app is currently temporarily unavailable. Please return to the apps to try again"
        >
          <Link to={PATH.APPS} className="capitalize">
            Explore apps
          </Link>
        </EmptyContent>
      );
    }

    return (
      <Theme.Container className="mx-auto flex max-w-[1328px] items-start gap-8 px-8 py-12">
        <PlatformAppLogo src={logo} size="lg" />
        <div className="flex-1">
          <h2 className="text-h4">{title}</h2>
          <p className="mt-1 text-neural-04">{shortDescription}</p>
          <PlatformAppTemplateVersion
            pubDate={pubDate}
            version={version}
            latestVersion={latestVersion}
            versions={versions}
          />
          <div className="mb-16 mt-6 flex gap-4">
            <Link
              to={path.installApp(appName, normalizedVersion)}
              className="px-10"
            >
              Install
            </Link>
            <Link
              theme
              disabled={!installedAppCount}
              to={`${PATH.APPS_INSTALLED}/${appName}`}
            >
              <Button
                stopPropagation={false}
                disabled={!installedAppCount}
                variant="primary-outline"
                className="capitalize"
              >
                View installed{' '}
                <Render if={installedAppCount > 0}>
                  ({installedAppCount})
                </Render>
              </Button>
            </Link>
          </div>
          <Render if={assets}>
            <PlatformAppAssets assets={assets} />
          </Render>
          <p className="mt-6 text-neural-04">{description}</p>

          <Render if={tags}>
            <div className="mt-6">
              <Tags restrictionAmount={null} tags={tags} />
            </div>
          </Render>

          <Render
            if={
              isLoadingRelatedApps ||
              relatedApps ||
              externalUrls ||
              documentationUrls
            }
          >
            <div className="mt-16 flex gap-6">
              <Render if={documentationUrls || externalUrls}>
                <div className="flex flex-1 flex-col gap-6">
                  <Render if={documentationUrls}>
                    <div className="flex basis-1/2 flex-col gap-3">
                      <p className="mb-2 text-h5">Documentation</p>
                      {documentationUrls.map(makeDocumentationLink)}
                    </div>
                  </Render>

                  <Render if={externalUrls}>
                    <div className="flex basis-1/2 flex-col gap-5">
                      <p className="text-h5">References</p>
                      {externalUrls.map(makeReferenceLink)}
                    </div>
                  </Render>
                </div>
              </Render>

              <Render if={isLoadingRelatedApps}>
                <div className="my-8 flex w-full flex-1 justify-center">
                  <ChaseSpinner color="black" className="h-8 w-8" />
                </div>
              </Render>

              <Render if={!isLoadingRelatedApps && relatedApps}>
                <div className="flex-1">
                  <p className="mb-1 text-h5 capitalize">Related</p>
                  <div className="mt-5 flex max-h-[420px] flex-col gap-5 overflow-y-auto pr-3">
                    {relatedApps.map(
                      ({ title, name, shortDescription, logo }) => (
                        <PlatformAppItem
                          key={title}
                          title={title}
                          image={logo}
                          description={shortDescription}
                          link={path.exploreApp(name)}
                        />
                      ),
                    )}
                  </div>
                </div>
              </Render>
            </div>
          </Render>

          <Render if={inputIntegrationTypes || outputIntegrationTypes}>
            <div className="mt-16 flex gap-6">
              <div className="flex flex-1 flex-col gap-5 rounded-xl bg-neural-01 px-10 py-6">
                <p className="mb-1 text-h5 capitalize">Input types</p>
                {inputIntegrationTypes.map(makeType)}
              </div>

              <div className="flex flex-1 flex-col gap-5 rounded-xl bg-neural-01 px-10 py-6">
                <p className="mb-1 text-h5 capitalize">Output types</p>
                {outputIntegrationTypes.map(makeType)}
              </div>
            </div>
          </Render>
        </div>
      </Theme.Container>
    );
  };

  return (
    <Layout backNavigation title={formattedTitle}>
      <Helmet title={formattedTitle} />
      <Layout.Content>{renderContent()}</Layout.Content>
    </Layout>
  );
};
